import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';

import BasicButton from '@/ui-components/BasicButton.vue';
import TitleList from '@/components/list/TitleList.vue';
import ListsEmptyPlaceholder from '@/components/watchlist/ListsEmptyPlaceholder.vue';
import { FilterBar } from '@/lazy-load-components';
import Spinner from '@/ui-components/Spinner.vue';

import { isEqual } from 'apollo-utilities';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { useFilterStore, useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';

import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';
import { CollectionType } from '@/enums/collection-type';
import { TitleListLayout } from '@/enums/title-list-layout';
import { MonetizationType, Platform, TitleFilter, TitleListSortingV2, TitleListTypeV2 } from '@/@types/graphql-types';
import {
	GetTitleListV2Document,
	GetTitleListV2Query,
	GetTitleListV2QueryVariables,
} from '../graphql/queries/GetTitleListV2.query';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { TitleOrder } from '@/enums/title-order';
import { getUsedFilters, isAnyFilterActive } from '@/helpers/filter-helper';
import { toastError } from '@/helpers/vfm/toast-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TabType } from '@/interfaces/inner-tabs';
import { TitleObjectType } from '@/interfaces/titles';


export default /*#__PURE__*/_defineComponent({
  __name: 'UserTitleList',
  props: {
    showContentTypeFilter: { type: Boolean, default: true }
  },
  setup(__props: any) {



const RestToGraphQLSortingMap: Partial<{ [key in TitleOrder]: TitleListSortingV2 }> = {
	[TitleOrder.POPULARITY]: TitleListSortingV2.Popular,
	[TitleOrder.TRENDING]: TitleListSortingV2.Trending,
	[TitleOrder.TMDB_POPULARITY]: TitleListSortingV2.TmdbPopularity,
	[TitleOrder.ALPHABETICAL]: TitleListSortingV2.Alphabetical,
	[TitleOrder.RANDOM]: TitleListSortingV2.Random,
	[TitleOrder.RELEASE_YEAR]: TitleListSortingV2.ReleaseYear,
	[TitleOrder.SCORE_IMDB]: TitleListSortingV2.ImdbScore,
	[TitleOrder.RUNTIME]: TitleListSortingV2.Runtime,
	[TitleOrder.LAST_ADDED]: TitleListSortingV2.LastAdded,
};

const isFirstPageLoaded = ref(false);

const router = useRouter();
const { t, tc } = useI18n();
const { isLoggedIn } = useUserStore();
const { queryParams } = useRoutingStore();
const { language, country } = useLanguageStore();
const { filters, selectedProviders } = useFilterStore();

const myListsFilters = computed(() => filters.value(CollectionType.MY_LISTS));

const graphqlFilters = computed<TitleFilter>(() => {
	return toGraphqlFilters(myListsFilters.value, selectedProviders.value(false));
});

const sortBy = computed<TitleListSortingV2>(() => {
	if (process.server) {
		const currentSortBy = queryParams.value.sort_by as TitleOrder;
		return RestToGraphQLSortingMap[currentSortBy] ?? TitleListSortingV2.LastAdded;
	}
	if (myListsFilters.value.sort_by) {
		return RestToGraphQLSortingMap[myListsFilters.value.sort_by as TitleOrder] ?? TitleListSortingV2.LastAdded;
	}
	return TitleListSortingV2.LastAdded;
});

const titleListType = computed(() => {
	switch (myListsFilters.value.inner_tab) {
		case TabType.LIKELIST:
			return TitleListTypeV2.Likelist;
		case TabType.SEENLIST:
			return TitleListTypeV2.Seenlist;
		default:
			return TitleListTypeV2.Watchlist;
	}
});

const randomSeed = computed(() => {
	const isRandomSort = sortBy.value === TitleListSortingV2.Random;
	if (process.server) {
		const randomSeed = Number(queryParams.value.sorting_random_seed) || 1;
		return isRandomSort ? randomSeed : 0;
	}
	const randomSeed = myListsFilters.value.sorting_random_seed || 1;
	return isRandomSort ? randomSeed : 0;
});

const currentListLayout = computed(() => myListsFilters.value.list_layout || TitleListLayout.CARD);

const queryVariables = computed<GetTitleListV2QueryVariables>(() => ({
	titleListFilter: {
		...graphqlFilters.value,
		includeTitlesWithoutUrl: true,
	},
	watchNowFilter: {
		packages: graphqlFilters.value.packages,
		monetizationTypes: graphqlFilters.value.monetizationTypes as MonetizationType[],
	},
	titleListSortBy: sortBy.value,
	language: language.value,
	country: country.value,
	titleListType: titleListType.value,
	sortRandomSeed: randomSeed.value,
	titleListAfterCursor: '',
	first: 20,
	includeOffers: currentListLayout.value === TitleListLayout.ROW,
	platform: Platform.Web,
}));

const { result, loading, fetchMore, refetch, onError, onResult } = useQuery<
	GetTitleListV2Query,
	GetTitleListV2QueryVariables
>(GetTitleListV2Document, queryVariables, {
	fetchPolicy: 'cache-and-network',
	prefetch: false,
});

const titlesListData = computed(() => result.value?.titleListV2);

onError(e => {
	console.error(e);
	if (process.client) {
		showErrorToast();
	}
});

onResult(() => {
	isFirstPageLoaded.value = true;
});

watch(
	() => isLoggedIn.value,
	() => {
		isFirstPageLoaded.value = false;
		refetch();
	}
);

watch(
	() => [currentListLayout.value, graphqlFilters.value],
	([newLayout, newFilters], [oldLayout, oldFilters]) => {
		if (!isEqual(newLayout, oldLayout) || !isEqual(newFilters, oldFilters)) {
			isFirstPageLoaded.value = false;
		}
	}
);

const watchlistTitles = computed(() => titlesListData.value?.edges || []);
const titleAmount = computed(() => titlesListData.value?.totalCount ?? 0);
const isAtEndOfList = computed(() => !titlesListData.value?.pageInfo?.hasNextPage);
const hasActiveFilter = computed(() => isAnyFilterActive(getUsedFilters(), myListsFilters.value));
const isWatchlistTab = computed(() => myListsFilters.value.inner_tab === TabType.WATCHLIST);
const isTvShowsTab = computed(() => myListsFilters.value.content_types.includes(TitleObjectType.SHOW));
const isMoviesTab = computed(() => myListsFilters.value.content_types.includes(TitleObjectType.MOVIE));

const emptyPlaceholderTranslations = computed(() => {
	const useTVShowTrackingTranslation = t('WEBAPP_LISTS_USE_TV_SHOW_TRACKING', {
		tvShowTracking: tc('WEBAPP_LISTS_TV_SHOW_TRACKING'),
	});

	switch (myListsFilters.value.inner_tab) {
		case TabType.LIKELIST:
			return {
				title: tc('WEBAPP_LISTS_EMPTY_WATCHLIST_LIKELIST_TITLE'),
				message: tc('WEBAPP_LISTS_EMPTY_WATCHLIST_LIKELIST_MESSAGE'),
			};
		case TabType.SEENLIST: {
			let seenMessage = tc('WEBAPP_LISTS_EMPTY_LISTS_ALL_SEENLIST_MESSAGE');
			if (isTvShowsTab.value) {
				seenMessage = t('WEBAPP_LISTS_EMPTY_SHOW_SEENLIST_MESSAGE', {
					useTvShowTracking: useTVShowTrackingTranslation,
				}) as string;
			} else if (isMoviesTab.value) {
				seenMessage = tc('WEBAPP_LISTS_EMPTY_MOVIE_SEENLIST_MESSAGE');
			}
			return {
				title: tc('WEBAPP_LISTS_EMPTY_WATCHLIST_SEEN_TITLE'),
				message: seenMessage,
			};
		}
		default: {
			let title = tc('WEBAPP_LISTS_EMPTY_WATCHLIST_HAVENT_STARTED_TITLE');
			let message = t('WEBAPP_LISTS_EMPTY_ALL_WATCHLIST_MESSAGE', {
				useTvShowTracking: useTVShowTrackingTranslation,
			});
			if (isTvShowsTab.value) {
				message = t('WEBAPP_LISTS_EMPTY_SHOW_WATCHLIST_MESSAGE', {
					useTvShowTracking: useTVShowTrackingTranslation,
				}) as string;
			} else if (isMoviesTab.value) {
				message = tc('WEBAPP_LISTS_EMPTY_MOVIE_WATCHLIST_MESSAGE');
			} else {
				title = tc('WEBAPP_LISTS_EMPTY_ALL_WATCHLIST_TITLE');
			}
			return { title, message };
		}
	}
});

async function onLoadNext({ event }: { event: any }) {
	if (!event) return;

	try {
		await fetchMore({
			variables: { titleListAfterCursor: titlesListData.value?.pageInfo?.endCursor },
			updateQuery: (previousResult, { fetchMoreResult }) => {
				if (!fetchMoreResult) return previousResult;

				const previousEdges = previousResult.titleListV2.edges ?? [];
				const fetchMoreEdges = fetchMoreResult.titleListV2.edges ?? [];

				return {
					titleListV2: {
						__typename: previousResult.titleListV2.__typename,
						totalCount: fetchMoreResult?.titleListV2.totalCount,
						edges: [...previousEdges, ...fetchMoreEdges],
						pageInfo: fetchMoreResult?.titleListV2.pageInfo,
					},
				};
			},
		});
	} catch (e: any) {
		captureMessageForSentry(
			'[Graphql User Titles List Load Next Issue]',
			{ error: e, where: 'Component: UserTitleList, method: onLoadNext' },
			'error'
		);
	} finally {
		if (event.target) {
			event.target.complete(watchlistTitles.value.length);
		}
	}
}

function navigateToTvShowTracking() {
	router.push({ name: 'app.lists.tv-show-tracking' });
}

function trackClick(payload: TitleEventPayloadGraphql) {
	trackTitleEventGraphql('title_clicked', payload, 'watchlist');
}

function showErrorToast() {
	toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
}

return { __sfc: true,RestToGraphQLSortingMap, isFirstPageLoaded, router, t, tc, isLoggedIn, queryParams, language, country, filters, selectedProviders, myListsFilters, graphqlFilters, sortBy, titleListType, randomSeed, currentListLayout, queryVariables, result, loading, fetchMore, refetch, onError, onResult, titlesListData, watchlistTitles, titleAmount, isAtEndOfList, hasActiveFilter, isWatchlistTab, isTvShowsTab, isMoviesTab, emptyPlaceholderTranslations, onLoadNext, navigateToTvShowTracking, trackClick, showErrorToast, BasicButton, TitleList, ListsEmptyPlaceholder, FilterBar, Spinner, CollectionType }
}

})